import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { useAuth } from '../context/AuthContext';

const PaymentTrack = () => {
    const { user } = useAuth();
    const [payments, setPayments] = useState([]);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [terms, setTerms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [selectedSession, setSelectedSession] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const db = getDatabase();

    useEffect(() => {
        // Fetch initial dropdown data
        const schoolInfoRef = ref(db, 'school_info');
        onValue(schoolInfoRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setSessions(data.sessions || []);
                setTerms(data.terms || []);
                setClasses(data.classes || []);
            }
        });

        // Fetch all payments
        const paymentsRef = ref(db, 'payments');
        onValue(paymentsRef, (snapshot) => {
            const allPayments = [];
            snapshot.forEach(sessionSnap => {
                const session = sessionSnap.key;
                sessionSnap.forEach(termSnap => {
                    const term = termSnap.key;
                    termSnap.forEach(userSnap => {
                        userSnap.forEach(paymentSnap => {
                            allPayments.push({ ...paymentSnap.val(), session, term });
                        });
                    });
                });
            });
            setPayments(allPayments);
            setFilteredPayments(allPayments);
            setLoading(false);
        });
    }, [db]);

    useEffect(() => {
        let filtered = payments;
        if (selectedSession) {
            filtered = filtered.filter(p => p.session === selectedSession);
        }
        if (selectedTerm) {
            filtered = filtered.filter(p => p.term === selectedTerm);
        }
        if (selectedClass) {
            filtered = filtered.filter(p => p.class === selectedClass);
        }
        setFilteredPayments(filtered);
    }, [selectedSession, selectedTerm, selectedClass, payments]);

    // Role-based access control
    const authorizedRoles = ['admin', 'principal', 'cashier'];
    if (!user || !authorizedRoles.includes(user.role?.toLowerCase())) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center p-8 bg-white shadow-md rounded-lg">
                    <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
                    <p className="mt-2">You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-center p-8">Loading payment records...</div>;
    }

    const formatCurrency = (num) => `₦${(num || 0).toLocaleString()}`;

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
            <div className="max-w-7xl mx-auto bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center text-blue-800 mb-6">Track Student Payments</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-4 border-b">
                    <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} className="p-2 border rounded">
                        <option value="">All Sessions</option>
                        {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)} className="p-2 border rounded">
                        <option value="">All Terms</option>
                        {terms.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="p-2 border rounded">
                        <option value="">All Classes</option>
                        {classes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead className="bg-gray-800 text-white">
                            <tr>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Student Name</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Reg No</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Class</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Date</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Amount Paid</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Balance</th>
                                <th className="py-3 px-4 uppercase font-semibold text-sm">Receipt No</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-700">
                            {filteredPayments.length > 0 ? (
                                filteredPayments.map((payment, index) => (
                                    <tr key={index} className="border-b hover:bg-gray-100">
                                        <td className="py-3 px-4">{payment.studentName}</td>
                                        <td className="py-3 px-4">{payment.regNo}</td>
                                        <td className="py-3 px-4">{payment.class}</td>
                                        <td className="py-3 px-4">{new Date(payment.date).toLocaleDateString()}</td>
                                        <td className="py-3 px-4 text-right">{formatCurrency(payment.amountPaid)}</td>
                                        <td className={`py-3 px-4 text-right font-bold ${payment.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {formatCurrency(payment.outstanding)}
                                        </td>
                                        <td className="py-3 px-4">{payment.receiptNo}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">No payment records found for the selected filters.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PaymentTrack;
